import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import axios from "axios";
import { API_CONFIG, API_ENDPOINTS } from "../constants/apiConfig";
import logo from "../assets/images/eb_logo.png";
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  // Clear alerts after 5 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (error || success) {
      setShowAlert(true);
      timer = setTimeout(() => {
        setShowAlert(false);
        setError(null);
        setSuccess(false);
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [error, success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await axios.post(
        `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.REQUEST_RESET}`,
        { email }
      );
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to process your request. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f7fb",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 400,
          p: 4,
          borderRadius: 2,
        }}
      >
         <img src={logo} style={{width:'30%'}}/>
         <Typography variant="h5" fontWeight={700} sx={{ mb: 3,textAlign:'center',mt:2 }}>
          Forgot Password
        </Typography>

        {showAlert && error && (
          <Alert 
            variant="filled" 
            severity="error" 
            sx={{ mt: 2, color: '#ffff', mb: 2 }}
            onClose={() => setShowAlert(false)}
          >
            {error}
          </Alert>
        )}
        
        {showAlert && success && (
          <Alert 
            variant="filled" 
            severity="success" 
            sx={{ mt: 2, color: '#ffff', mb: 2 }}
            onClose={() => setShowAlert(false)}
          >
            You will receive a password reset link.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="caption"
                display="block"
    sx={{ mb: 0.5,color:'#3A3541',textAlign:'left' }}
              >
                Email
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  sx: {
                    bgcolor: "#f5f7fb",
                    borderRadius: "6px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    fontSize: "1rem",
                    color: "#3A3541"
                  }
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                backgroundColor: '#0079C2',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#0062a3',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 121, 194, 0.7)',
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                position: 'relative',
                minHeight: '48px'
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} color="inherit" thickness={5} />
                  <span>Submitting...</span>
                </>
              ) : (
                'Submit'
              )}
            </Button>
            
            
          </Stack>
        </Box>
        
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
